import BeraIcon from '@apps-orangefi/assets/images/tokens/bera.svg'
import PlsSykIcon from '@apps-orangefi/assets/images/tokens/plssyk.svg'
import SykIcon from '@apps-orangefi/assets/images/tokens/syk.svg'
import { BN } from '@apps-orangefi/lib'
import { DexPair, fetchDexPair } from '@apps-orangefi/lib/api'
import { getDexScreenParam } from '@apps-orangefi/lib/utils'
import { useCurrentEpoch, useEpochRewards, Reward } from '@apps-orangefi/wagmi/hooks'
import { useQuery } from '@tanstack/react-query'
import { chain, isEmpty, isNil } from 'lodash'
import { StaticImageData } from 'next/image'
import { useMemo } from 'react'
import { zeroAddress } from 'viem'

const YEAR_IN_SECONDS = 31536000
const WEEK_IN_SECONDS = 604800
const SYK_TOKEN_ADDRESS = '0xACC51FFDeF63fB0c014c882267C3A17261A5eD50' as AddressType
// const XSYK_TOKEN_ADDRESS = '0x50E04E222Fc1be96E94E86AcF1136cB0E97E1d40' as AddressType
const PLSSYK_TOKEN_ADDRESS = '0x68D6d2545f14751baF36c417c2CC7cdf8dA8a15b' as AddressType
const WBERA_TOKEN_ADDRESS = '0x6969696969696969696969696969696969696969' as AddressType

type IVault = { vaultAddress: AddressType; strykeGaugeId: AddressType | undefined; tvl: BN }

type StrykeRewardAPR = IVault & {
  epoch: number
  reward: BN
  totalRewardApr: BN
  aprList: {
    iconUrl: StaticImageData
    symbol: string
    tokenAddress: AddressType
    apr: BN
  }[]
}

export const useStrykeRewardAPR = (
  distributorAddress: AddressType | undefined,
  vaultList: IVault[],
  chainId: number
) => {
  const {
    currentEpoch,
    epochLength,
    controllerAddress,
    isFetching: isFetchingCurrentEpoch,
  } = useCurrentEpoch(distributorAddress)

  const dexScreenParamList = getDexScreenParam(chainId)
  const { data: dataDexPairList, isFetching: isFetchingDexScreener } = useQuery({
    queryKey: ['fetchDexPair', dexScreenParamList!],
    queryFn: fetchDexPair,
    refetchOnWindowFocus: false,
    enabled: !isEmpty(dexScreenParamList),
  })

  const rewardTargets = useMemo(() => {
    if (!currentEpoch) {
      return []
    }
    return vaultList
      .filter(vault => !!vault.strykeGaugeId)
      .map(vault => ({
        vaultAddress: vault.vaultAddress,
        strykeGaugeId: vault.strykeGaugeId!,
        epoch: currentEpoch - 1,
      }))
  }, [vaultList, currentEpoch])

  const { rewards, isFetching: isFetchingRewards } = useEpochRewards(
    controllerAddress,
    rewardTargets
  )

  const strykeRewardsApr = useMemo(() => {
    if (isEmpty(dexScreenParamList) || !dataDexPairList || isEmpty(dataDexPairList)) {
      return []
    }
    const chainKey = dexScreenParamList[0].chainKey
    return calculateRewards(chainKey, vaultList, dataDexPairList, rewards ?? [], epochLength)
  }, [
    dexScreenParamList,
    JSON.stringify(vaultList),
    JSON.stringify(rewards),
    epochLength,
    dataDexPairList,
  ])

  return {
    strykeRewardsApr,
    isFetching: isFetchingCurrentEpoch || isFetchingRewards || isFetchingDexScreener,
  }
}

// Previously, the calculation assumed a single reward token with a single price.
// However, it needs to be updated to consider multiple reward tokens.

// Previous implementation:
// - SYK and xSYK were treated as having the same quantity and value.
// - Calculations were based on the pre-split amount of SYK and its price in USD.

// Updated implementation:
// - Store the allocation ratio of SYK and plsSYK in the configuration.
// - Split the reward amount based on the SYK and plsSYK allocation, then calculate rewardUSD using each token's price in USD.
// - Sum up the rewardUSD values, then divide by TVL to compute the APR.

const calculateRewards = (
  chainKey: string,
  vaultList: IVault[],
  dexPairList: DexPair[],
  contractRewards: Reward[],
  contractEpochLength?: number // arbitrum用の動的なepochLength
) => {
  const rewardTokens = getRewardTokensForChain(chainKey)
  const epochLength = getEpochLengthForChain(chainKey, contractEpochLength)
  const rewards = getRewardsForChain(chainKey, contractRewards)

  const rewardsWithApr = calculateCommonRewards(
    rewards,
    vaultList,
    epochLength,
    dexPairList,
    rewardTokens
  )
  return rewardsWithApr
}

const calculateCommonRewards = (
  rewards: Reward[],
  vaultList: IVault[],
  epochLength: number,
  dexPairList: DexPair[],
  rewardTokens: { iconUrl: any; symbol: string; tokenAddress: AddressType; rewardRate: number }[]
) => {
  return vaultList.map(vault => {
    const reward = rewards.find(r => r.vaultAddress === vault.vaultAddress)
    const tvl = vault?.tvl

    const base = {
      ...vault,
      epoch: reward?.epoch ?? 0,
      reward: reward?.reward ?? new BN(0),
    }

    if (!tvl || !tvl.gt(0) || isNil(epochLength) || epochLength <= 0) {
      return {
        ...base,
        totalRewardApr: new BN(0),
        aprList: rewardTokens.map(rewardToken => ({
          ...rewardToken,
          apr: new BN(0),
          rewardUsd: new BN(0),
          priceUsd: new BN(0),
        })),
      }
    }

    const rewardTokenList = rewardTokens.map(rewardToken => {
      const dexPair = dexPairList.find(
        pair => pair.baseToken.address.toLowerCase() === rewardToken.tokenAddress.toLowerCase()
      )
      const priceUsd = dexPair?.priceUsd ?? new BN(0)
      return {
        ...rewardToken,
        rewardUsd:
          reward?.reward.multipliedBy(rewardToken.rewardRate).multipliedBy(priceUsd) ?? new BN(0),
        priceUsd,
      }
    })
    const totalRewardUsd = rewardTokenList.reduce((acc, cur) => acc.plus(cur.rewardUsd), new BN(0))

    const totalRewardApr = totalRewardUsd
      .multipliedBy(YEAR_IN_SECONDS / epochLength)
      .div(tvl)
      .multipliedBy(100)

    return {
      ...base,
      totalRewardApr,
      aprList: rewardTokenList.map(rewardToken => ({
        ...rewardToken,
        apr: totalRewardApr.multipliedBy(rewardToken.rewardUsd.div(totalRewardUsd)), // divide APR by reward usd ratio
      })),
    }
  })
}

const getRewardTokensForChain = (chainKey: string) => {
  switch (chainKey) {
    case 'arbitrum':
      return [
        { iconUrl: SykIcon, symbol: 'SYK', tokenAddress: SYK_TOKEN_ADDRESS, rewardRate: 0.5 },
        {
          iconUrl: PlsSykIcon,
          symbol: 'plsSYK',
          tokenAddress: PLSSYK_TOKEN_ADDRESS,
          rewardRate: 0.5,
        },
      ]
    case 'berachain':
      return [
        { iconUrl: BeraIcon, symbol: 'WBERA', tokenAddress: WBERA_TOKEN_ADDRESS, rewardRate: 1 },
      ]
    default:
      return []
  }
}

const getEpochLengthForChain = (chainKey: string, contractEpochLength?: number) => {
  switch (chainKey) {
    case 'arbitrum':
      return contractEpochLength ?? 0 // use contract value on arbitrum
    case 'berachain':
      return WEEK_IN_SECONDS // use fixed value on berachain
    default:
      return 0
  }
}

const getRewardsForChain = (chainKey: string, rewards: Reward[]): Reward[] => {
  switch (chainKey) {
    case 'arbitrum':
      return rewards
    case 'berachain':
      return [
        {
          vaultAddress: '0x2f852e3102357ffc2283a974d42bd4b4cae9b5aa',
          strykeGaugeId: zeroAddress,
          epoch: 0,
          reward: new BN(875),
        },
        {
          vaultAddress: '0xf45747fde3586563e1fb9d50f815b70822176a46',
          strykeGaugeId: zeroAddress,
          epoch: 0,
          reward: new BN(625),
        },
        {
          vaultAddress: '0x4ea6efbecaabd7ba6e13f4847a518fa34729ac9f',
          strykeGaugeId: zeroAddress,
          epoch: 0,
          reward: new BN(375),
        },
      ]
    default:
      return []
  }
}
